// Macro conditions for the Marker Lab context panel: Fed stance, labor-market
// prints, and the dollar vs major currencies. There is no deployed macro route
// yet, so after trying /api/finance/research/macro the client composes the
// panel itself — FX, the dollar index, the 10Y yield, and VIX come live from
// the existing quote endpoint (Yahoo serves currency/index tickers), while the
// Fed and jobs figures are curated samples until a real feed is wired up.

import type { EquityQuote } from '../types/finance'
import type {
  FxRateReading,
  JobsSentiment,
  MacroConditions,
  MacroMarketReading,
  MacroStanceLabel,
  SuggestedIndicator,
} from '../types/research'
import { apiGet } from './api-client'

const USE_MOCK = !import.meta.env.VITE_FINANCE_API_URL

let _usingMockData = USE_MOCK
export function isMacroMock(): boolean {
  return _usingMockData
}

// ─── Calendars ───────────────────────────────────────────────

/** FOMC decision days (second day of each meeting), published in advance. */
const FOMC_DECISION_DATES = [
  '2026-01-28',
  '2026-03-18',
  '2026-04-29',
  '2026-06-17',
  '2026-07-29',
  '2026-09-16',
  '2026-10-28',
  '2026-12-09',
  '2027-01-27',
]

function firstFridayOfMonth(year: number, monthIdx: number): Date {
  const d = new Date(year, monthIdx, 1)
  const offset = (5 - d.getDay() + 7) % 7
  return new Date(year, monthIdx, 1 + offset)
}

function iso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Employment Situation is released the first Friday of the following month. */
function jobsReportDates(today: Date): { last: string; next: string } {
  const thisMonth = firstFridayOfMonth(today.getFullYear(), today.getMonth())
  if (today.getTime() >= thisMonth.getTime()) {
    return {
      last: iso(thisMonth),
      next: iso(firstFridayOfMonth(today.getFullYear(), today.getMonth() + 1)),
    }
  }
  return {
    last: iso(firstFridayOfMonth(today.getFullYear(), today.getMonth() - 1)),
    next: iso(thisMonth),
  }
}

function fomcDates(today: Date): { last: string; next: string } {
  const t = iso(today)
  const last = [...FOMC_DECISION_DATES].reverse().find((d) => d <= t) ?? FOMC_DECISION_DATES[0]
  const next = FOMC_DECISION_DATES.find((d) => d > t) ?? FOMC_DECISION_DATES[FOMC_DECISION_DATES.length - 1]
  return { last, next }
}

// ─── Curated / fallback figures ──────────────────────────────

function stanceLabel(stance: number): MacroStanceLabel {
  if (stance <= -0.6) return 'Dovish'
  if (stance <= -0.2) return 'Leans dovish'
  if (stance < 0.2) return 'Neutral'
  if (stance < 0.6) return 'Leans hawkish'
  return 'Hawkish'
}

function jobsSentimentFor(surpriseK: number): JobsSentiment {
  if (surpriseK >= 30) return 'strong'
  if (surpriseK <= -30) return 'soft'
  return 'inline'
}

interface FxDef {
  pair: string
  label: string
  usdUpWhenHigher: boolean
  fallbackPrice: number
}

const FX_PAIRS: FxDef[] = [
  { pair: 'EURUSD=X', label: 'EUR/USD', usdUpWhenHigher: false, fallbackPrice: 1.166 },
  { pair: 'USDJPY=X', label: 'USD/JPY', usdUpWhenHigher: true, fallbackPrice: 159.35 },
  { pair: 'GBPUSD=X', label: 'GBP/USD', usdUpWhenHigher: false, fallbackPrice: 1.359 },
  { pair: 'USDMXN=X', label: 'USD/MXN', usdUpWhenHigher: true, fallbackPrice: 16.96 },
  { pair: 'USDCNY=X', label: 'USD/CNY', usdUpWhenHigher: true, fallbackPrice: 6.72 },
  { pair: 'AUDUSD=X', label: 'AUD/USD', usdUpWhenHigher: false, fallbackPrice: 0.719 },
]

const SUGGESTED_INDICATORS: SuggestedIndicator[] = [
  { id: 'cpi', label: 'CPI / Core PCE inflation', why: 'The other half of the Fed mandate — surprise vs consensus moves rate expectations directly.' },
  { id: 'curve', label: '2s10s yield-curve spread', why: 'Recession / regime signal; steepening vs inversion changes which sectors lead.' },
  { id: 'pmi', label: 'ISM Manufacturing & Services PMI', why: 'Fastest broad read on the business cycle; sub-50 prints reprice cyclicals.' },
  { id: 'claims', label: 'Weekly initial jobless claims', why: 'Higher-frequency labor signal between monthly payroll reports.' },
  { id: 'umich', label: 'Consumer sentiment (UMich) & retail sales', why: 'Consumer demand pulse for discretionary and staples names.' },
  { id: 'hy-spread', label: 'High-yield credit spreads', why: 'Risk appetite / financial-conditions gauge that leads equity drawdowns.' },
  { id: 'commodities', label: 'WTI crude & gold', why: 'Inflation pass-through and safe-haven positioning; direct driver for energy and materials.' },
  { id: 'housing', label: 'Housing starts & mortgage rates', why: 'Rate-sensitivity check for real estate, builders, and banks.' },
]

function curatedMacro(today: Date): MacroConditions {
  const fomc = fomcDates(today)
  const jobs = jobsReportDates(today)
  const nfp = 115
  const consensus = 140
  const surprise = nfp - consensus
  const stance = 0.25
  return {
    asOf: today.toISOString(),
    fedJobsCurated: true,
    fed: {
      targetRangePct: [3.5, 3.75],
      effectiveRatePct: 3.58,
      lastMove: 'hold',
      lastMoveBps: 0,
      lastDecisionDate: fomc.last,
      stance,
      stanceLabel: stanceLabel(stance),
      nextMeetingDate: fomc.next,
      note: 'Statement kept rates unchanged; press conference stressed patience while services inflation stays sticky.',
    },
    jobs: {
      nonfarmPayrollsK: nfp,
      consensusK: consensus,
      surpriseK: surprise,
      unemploymentRatePct: 4.4,
      avgHourlyEarningsYoYPct: 3.8,
      sentiment: jobsSentimentFor(surprise),
      lastReportDate: jobs.last,
      nextReportDate: jobs.next,
      note: 'Hiring cooled below consensus with a slight uptick in unemployment — a labor market easing, not cracking.',
    },
    dollarIndex: { label: 'US Dollar Index (DXY)', value: 99.2, changePercent: -0.02, unit: '', live: false },
    fx: FX_PAIRS.map((f) => ({
      pair: f.pair,
      label: f.label,
      price: f.fallbackPrice,
      changePercent: 0,
      usdUpWhenHigher: f.usdUpWhenHigher,
      live: false,
    })),
    markets: [
      { label: '10Y Treasury yield', value: 4.66, changePercent: null, unit: '%', live: false },
      { label: 'VIX', value: 15.2, changePercent: null, unit: '', live: false },
    ],
    suggested: SUGGESTED_INDICATORS,
  }
}

// ─── Live composition ────────────────────────────────────────

async function liveQuote(symbol: string): Promise<EquityQuote | null> {
  try {
    return await apiGet<EquityQuote>(`/api/finance/quote?symbol=${encodeURIComponent(symbol)}`)
  } catch {
    return null
  }
}

async function composeLiveMacro(today: Date): Promise<MacroConditions> {
  const base = curatedMacro(today)
  const [fxQuotes, dxy, tnx, vix] = await Promise.all([
    Promise.all(FX_PAIRS.map((f) => liveQuote(f.pair))),
    liveQuote('DX-Y.NYB'),
    liveQuote('^TNX'),
    liveQuote('^VIX'),
  ])

  const fx: FxRateReading[] = FX_PAIRS.map((f, i) => {
    const q = fxQuotes[i]
    if (!q) return base.fx[i]
    return {
      pair: f.pair,
      label: f.label,
      price: q.currentPrice,
      changePercent: q.changePercent,
      usdUpWhenHigher: f.usdUpWhenHigher,
      live: true,
    }
  })

  const anyLive = fx.some((f) => f.live) || dxy != null || tnx != null || vix != null
  if (!anyLive) throw new Error('no live macro quotes')

  const markets: MacroMarketReading[] = [
    tnx
      ? { label: '10Y Treasury yield', value: tnx.currentPrice, changePercent: tnx.changePercent, unit: '%', live: true }
      : base.markets[0],
    vix
      ? { label: 'VIX', value: vix.currentPrice, changePercent: vix.changePercent, unit: '', live: true }
      : base.markets[1],
  ]

  return {
    ...base,
    fx,
    dollarIndex: dxy
      ? { label: 'US Dollar Index (DXY)', value: dxy.currentPrice, changePercent: dxy.changePercent, unit: '', live: true }
      : base.dollarIndex,
    markets,
  }
}

// ─── Entry point ─────────────────────────────────────────────

export async function getMacroConditions(): Promise<MacroConditions> {
  const today = new Date()
  if (USE_MOCK) {
    _usingMockData = true
    await new Promise((r) => setTimeout(r, 400))
    return curatedMacro(today)
  }
  try {
    const result = await apiGet<MacroConditions>('/api/finance/research/macro')
    _usingMockData = false
    return result
  } catch {
    // Macro route not deployed yet — compose from live quotes + curated figures.
  }
  try {
    const result = await composeLiveMacro(today)
    _usingMockData = false
    return result
  } catch {
    console.warn('Live macro composition failed, falling back to curated data')
    _usingMockData = true
    return curatedMacro(today)
  }
}
